import { useMutation } from "@apollo/client/react"
import { Check, Copy, ExternalLink } from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { ReactionPostFragment, EditorialSourceFragment } from "~/__generated__/graphql"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/graphql-error"
import { AgentReview, PangramReview } from "./agent-review"
import { SAVE, APPROVE } from "./editorial-queries"

export function PostEditor({
  post,
  source,
  onSaved,
  onClose,
  onDirtyChange,
}: {
  post?: ReactionPostFragment
  source?: EditorialSourceFragment | null
  onSaved: (id: string) => Promise<void>
  onClose: () => void
  onDirtyChange: (dirty: boolean) => void
}) {
  const [title, setTitle] = useState(post?.title ?? source?.title.slice(0, 250) ?? "")
  const [body, setBody] = useState(post?.body ?? "")
  const [version, setVersion] = useState(post?.lockVersion ?? 0)
  const [error, setError] = useState<Error | null>(null)
  const [approvalNote, setApprovalNote] = useState(post?.approvalNote ?? "")
  const [save, { loading: saving }] = useMutation(SAVE)
  const [approve, { loading: approving }] = useMutation(APPROVE)
  const editable = post?.editable ?? true
  const dirty =
    title !== (post?.title ?? source?.title.slice(0, 250) ?? "") || body !== (post?.body ?? "")
  const count = Array.from(body).length
  useEffect(() => {
    onDirtyChange(dirty)
    return () => onDirtyChange(false)
  }, [dirty, onDirtyChange])

  useEffect(() => {
    if (!dirty) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", warn)
    return () => window.removeEventListener("beforeunload", warn)
  }, [dirty])

  const handleSave = async () => {
    setError(null)
    try {
      const result = await save({
        variables: {
          input: {
            id: post?.id,
            expectedVersion: post ? version : undefined,
            sourceItemId: source?.id,
            title,
            body,
          },
        },
      })
      if (result.error) throw result.error
      if (result.data) {
        setTitle(result.data.contentPostSave.post.title)
        setVersion(result.data.contentPostSave.post.lockVersion)
        await onSaved(result.data.contentPostSave.post.id)
        toast.success("Draft saved")
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Could not save draft"))
    }
  }

  const handleApprove = async () => {
    if (!post) return
    setError(null)
    try {
      const result = await approve({
        variables: { input: { id: post.id, expectedVersion: version, approvalNote } },
      })
      if (result.error) throw result.error
      if (result.data) {
        setVersion(result.data.contentPostApprove.post.lockVersion)
        await onSaved(post.id)
        toast.success("Post approved")
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Could not approve post"))
    }
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body)
      toast.success("Post text copied")
    } catch {
      toast.error("Copy failed. Select the text and copy it manually.")
    }
  }

  return (
    <section className="rounded-2xl border bg-card p-5 sm:p-7" aria-label="Post editor">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            LinkedIn feed post
          </p>
          <h2 className="mt-2 text-xl font-semibold">
            {post ? "Review your words" : "Start a reaction"}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {post?.authorName ?? "Your post"} · {post?.status.toLowerCase() ?? "New draft"}
          </p>
        </div>
        <Button variant="ghost" onClick={onClose}>
          Back to inbox
        </Button>
      </div>
      {source && (
        <a
          href={source.url}
          target="_blank"
          rel="noreferrer"
          className="mb-6 flex items-center gap-2 text-sm underline underline-offset-4"
        >
          <ExternalLink className="size-4 shrink-0" />
          Read original source
        </a>
      )}
      {editable && post && (
        <AgentReview
          post={post}
          body={body}
          onSaved={() => onSaved(post.id)}
          onUse={(proposal) => {
            if (
              body.trim() &&
              body !== proposal &&
              !window.confirm(
                "Replace the text in the editor with this draft? Saved versions are kept."
              )
            )
              return
            setBody(proposal)
            document.getElementById("post-body")?.focus()
          }}
        />
      )}
      <label className="mb-2 block text-sm font-medium" htmlFor="post-title">
        Working title
      </label>
      <input
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-ring disabled:opacity-50"
        id="post-title"
        value={title}
        maxLength={250}
        disabled={!editable || saving || approving}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="The insight worth sharing"
      />
      <label className="mb-2 mt-5 block text-sm font-medium" htmlFor="post-body">
        Post text
      </label>
      <textarea
        id="post-body"
        rows={12}
        value={body}
        disabled={!editable || saving || approving}
        onChange={(e) => setBody(e.target.value)}
        className="w-full resize-y rounded-lg border bg-background p-4 text-sm leading-7 focus-visible:outline-ring"
        placeholder="Start with your own words. What did you notice, and what would help someone building this?"
      />
      <div className="mt-2 flex justify-between gap-3 text-xs text-muted-foreground">
        <span>
          {editable
            ? "Manual edits create a new version and clear approval."
            : "Only the author can edit or approve this post."}
        </span>
        <span className={count > 3000 ? "text-destructive" : ""}>
          {count.toLocaleString()} / 3,000
        </span>
      </div>
      {editable && post && (
        <>
          <PangramReview post={post} body={body} onSaved={() => onSaved(post.id)} />
          <label className="mt-4 block text-sm font-medium" htmlFor="approval-note">
            Author review note (optional)
          </label>
          <input
            id="approval-note"
            className="mt-2 w-full rounded-lg border bg-background p-3 text-sm"
            value={approvalNote}
            maxLength={1000}
            onChange={(event) => setApprovalNote(event.target.value)}
            placeholder="For example: I reviewed this and kept my phrasing despite the AI signal."
          />
        </>
      )}
      {error && (
        <div className="mt-4">
          <GraphqlError error={error} />
        </div>
      )}
      <div className="mt-5 flex flex-wrap gap-2">
        {editable && (
          <Button
            onClick={handleSave}
            disabled={saving || approving || !title.trim() || count > 3000 || (!!post && !dirty)}
          >
            {saving ? "Saving…" : "Save draft"}
          </Button>
        )}
        {editable && post && (
          <Button
            variant="outline"
            onClick={handleApprove}
            disabled={dirty || saving || approving || !body.trim() || post.status === "APPROVED"}
          >
            <Check className="size-4" />
            {approving ? "Approving…" : post.status === "APPROVED" ? "Approved" : "Approve post"}
          </Button>
        )}
        <Button variant="outline" onClick={copy} disabled={!body}>
          <Copy className="size-4" />
          Copy post text
        </Button>
      </div>
      {!!post?.revisions.length && (
        <details className="mt-6">
          <summary className="cursor-pointer text-sm font-medium">
            Version history · {post.revisions.length}
          </summary>
          <div className="mt-4 space-y-3">
            {post.revisions.map((revision) => (
              <details key={revision.id} className="rounded-lg border p-3">
                <summary className="cursor-pointer text-sm">
                  Version {revision.number} · {revision.title}
                </summary>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-6">
                  {revision.body || "Empty draft"}
                </p>
              </details>
            ))}
          </div>
        </details>
      )}
    </section>
  )
}
